import React from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock, Truck } from "lucide-react";
import { api } from "../lib/api";
import { isNative, flushPendingNativeToken, initNativePush } from "../lib/native";
import PwaInstallPrompt from "../components/PwaInstallPrompt";

export default function DriverLoginPage() {
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const nav = useNavigate();

  if (localStorage.getItem("ema_driver_token")) return <Navigate to="/driver" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/driver/login", { email, password });
      localStorage.setItem("ema_driver_token", data.token);
      localStorage.setItem("ema_driver_name", data.driver.name);
      // If running inside the native iOS/Android shell, flush the pending push token
      // (and try registration if it hasn't happened yet).
      if (isNative()) {
        flushPendingNativeToken().catch(() => {});
        initNativePush(() => data.token).catch(() => {});
      }
      toast.success(`Velkommen, ${data.driver.name}`);
      if (data.driver.onboarding_required) {
        nav("/driver/onboarding");
      } else {
        nav("/driver");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Forkert email eller password");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-brand-dark flex items-center justify-center p-5" data-testid="driver-login-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 justify-center mb-8">
          <img src="/brand/ema-logo-transparent.png" alt="" className="h-14" />
          <div className="leading-none">
            <div className="font-display font-bold text-2xl text-white">EMA Kurer<span className="text-brand-red">.</span></div>
            <div className="text-[10px] tracking-[0.25em] uppercase font-semibold text-white/50 mt-1">Chauffør-portal</div>
          </div>
        </div>

        <div className="bg-white p-8">
          <div className="kicker">Login</div>
          <h1 className="font-display font-bold text-2xl text-brand-dark mt-2">Dagens leveringer</h1>
          <p className="text-brand-text2 text-sm mt-2">Log ind for at se og opdatere dagens bookinger.</p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="driver-login-form">
            <div>
              <label className="label-flat">Email</label>
              <input type="email" inputMode="email" autoComplete="username" className="input-flat" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="driver-input-email" required />
            </div>
            <div>
              <label className="label-flat">Password</label>
              <input type="password" autoComplete="current-password" className="input-flat" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="driver-input-password" required />
            </div>
            <button type="submit" className="btn-primary w-full justify-center" disabled={busy} data-testid="driver-login-submit">
              {busy ? <><Loader2 className="animate-spin" size={16} /> Logger ind...</> : <><Lock size={16} /> Log ind</>}
            </button>
          </form>

          <div className="mt-6 pt-5 border-t border-brand text-xs text-brand-text2 flex items-center gap-2">
            <Truck size={14} className="text-brand-red" />
            <span>Brug for chauffør-konto? Kontakt admin på <a href="mailto:kontakt@emakurer.dk" className="text-brand-red font-semibold">kontakt@emakurer.dk</a></span>
          </div>
        </div>
      </div>
      <PwaInstallPrompt />
    </div>
  );
}
